import {useNavigation} from '@react-navigation/native';
import React, {useEffect, useState} from 'react';
import {
  Alert,
  Image,
  ImageBackground,
  Linking,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import ButtonComponent from '../../../components/ButtonComponent/ButtonComponent';
import Input from '../../../components/Input/Input';
import {Images} from '../../../constants/Images';
import {styles} from './styles';
import {useAuthStore} from '../../../store/AuthStore';
import AuthService from '../../../services/AuthService/AuthService';
import {CredentialsType} from '../../../types/Auth';
import {useUserStore} from '../../../store/UserStore';
import {NavigationProps} from '../../../navigation/Routes';
import {AppRoutes} from '../../../navigation/Routes.types';
import {useLoadingStore} from '../../../store/LoadingStore';
import CheckBox from '../../../components/CheckBox/CheckBox';
import {useIsDesktop} from '../../../hooks/useIsDesktop';
import {useBiometricStore} from '../../../store/BiometricStore';
import {
  BiometryType,
  clearBiometricCredentials,
  clearCredentials,
  clearLegacyCredentials,
  clearPlainCredentials,
  getBiometryLabel,
  getCredentialsBiometric,
  getCredentialsPlain,
  getSupportedBiometry,
  saveCredentialsBiometric,
  saveCredentialsPlain,
} from '../../../utils/biometricAuth';

const Login = () => {
  const isDesktop = useIsDesktop();
  const [credentials, setCredentials] = useState<CredentialsType>({username: '', password: ''});
  const [fieldErrors, setFieldErrors] = useState<{username?: string; password?: string}>({});
  const [isRememberMe, setIsRememberMe] = useState(false);
  const [isBiometricChecked, setIsBiometricChecked] = useState(false);
  const [biometryType, setBiometryType] = useState<BiometryType>(null);
  const {setUser, setUserType, setNetworkAccountId, setCatalogBlocked, setTemporaryPasswordLogin} = useUserStore();
  const {navigate} = useNavigation<NavigationProps>();
  const {showLoading, hideLoading} = useLoadingStore();
  const {setToken, defUser, setIsRefresing, token} = useAuthStore();
  const {biometricEnabled, setBiometricEnabled} = useBiometricStore();
  const [hydrated, setHydrated] = useState(
    () =>
      useBiometricStore.persist.hasHydrated() &&
      useAuthStore.persist.hasHydrated(),
  );

  const biometryLabel = getBiometryLabel(biometryType);

  useEffect(() => {
    if (hydrated) {
      return;
    }
    const check = () => {
      if (
        useBiometricStore.persist.hasHydrated() &&
        useAuthStore.persist.hasHydrated()
      ) {
        setHydrated(true);
      }
    };
    const unsubBiometric = useBiometricStore.persist.onFinishHydration(check);
    const unsubAuth = useAuthStore.persist.onFinishHydration(check);
    check();
    return () => {
      unsubBiometric();
      unsubAuth();
    };
  }, [hydrated]);

  const handleNavigateForgetPassword = () => {
    navigate(AppRoutes.RECOVER_PASSWORD);
  };

  const handleNavigateRegisterCode = () => {
    navigate(AppRoutes.REGISTER_CODE);
  };

  const handleLogin = async (usernameArg?: string, passwordArg?: string) => {
    // onPress passa um evento como primeiro argumento; só usamos override se for string.
    const username =
      typeof usernameArg === 'string' ? usernameArg : credentials.username;
    const password =
      typeof passwordArg === 'string' ? passwordArg : credentials.password;

    setIsRefresing(false);
    try {
      if (username && password) {
        setFieldErrors({});
        showLoading();
        const response = await AuthService.loginService(username, password);

        console.log('[Login] API response:', JSON.stringify(response, null, 2));
        let user = response?.user;
        const userType = response?.user_type;

        setUserType(userType);

        if (userType === 'network') {
          // For network accounts, store the network account ID
          setNetworkAccountId(user.id);
          // Network accounts have current_client, set the figure
          if (user.current_client) {
            (user.current_client as any).figure = response?.client_figure;
          }
        } else {
          // For regular users, set current_client figure
          const regularUser = user as any; // Type assertion for regular user
          if (regularUser.current_client) {
            regularUser.current_client.figure = response?.client_figure;
          }
        }

        setUser(user);
        defUser(user);
        setToken(response?.access_token);
        setCatalogBlocked(response?.catalog_blocked ?? false, response?.catalog_blocked_message);
        setTemporaryPasswordLogin((user as any)?.temporary_password_login ?? false);

        // Persistência das credenciais: biometria tem precedência sobre "Lembrar de mim".
        if (isBiometricChecked && biometryType) {
          await saveCredentialsBiometric(username, password);
          setBiometricEnabled(true);
        } else {
          await clearBiometricCredentials();
          if (biometricEnabled) {
            setBiometricEnabled(false);
          }
          if (isRememberMe) {
            await saveCredentialsPlain(username, password);
          } else {
            await clearPlainCredentials();
          }
        }
      } else {
        setFieldErrors({
          username: username ? undefined : 'Campo obrigatório',
          password: password ? undefined : 'Campo obrigatório',
        });
        Alert.alert('Preencha todos os campos!');
      }
    } catch (error: any) {
      Alert.alert(
        error?.response?.data?.message || 'Conecta Brasal',
        error?.response?.data?.details ||
          'Dados incorretos, informe de novo por favor.',
      );

      if (error?.response?.status === 422) {
        try {
          await clearCredentials();
        } catch (e) {
          console.error('Erro ao resetar Keychain:', e);
        }
        setIsRememberMe(false);
        setIsBiometricChecked(false);
        setBiometricEnabled(false);
        setCredentials(prev => ({
          ...prev,
          password: '',
        }));
      }
    } finally {
      hideLoading();
    }
  };

  const handleBiometricLogin = async () => {
    try {
      const creds = await getCredentialsBiometric(`Entrar com ${biometryLabel}`);
      if (creds?.username && creds?.password) {
        await handleLogin(creds.username, creds.password);
      } else {
        Alert.alert(
          'Conecta Brasal',
          'Nenhuma credencial salva. Entre com CNPJ/CPF e senha.',
        );
      }
    } catch (e) {
      Alert.alert(
        'Conecta Brasal',
        'Não foi possível autenticar com a biometria. Entre com sua senha.',
      );
    }
  };

  const handleNavigateResendToken = () => {
    navigate(AppRoutes.RESEND_TOKEN);
  };

  const handleNavigateNFSig = () => {
    navigate(AppRoutes.NFSIG);
  };

  useEffect(() => {
    // O AuthStore/BiometricStore são reidratados de forma assíncrona. Enquanto
    // isso não termina, o Routes monta o Login mesmo com sessão válida e o
    // `biometricEnabled` ainda vale false — ler o keychain aqui dispararia um
    // prompt de biometria indevido ao abrir o app. Só agimos após a hidratação
    // e apenas quando não há sessão válida.
    if (!hydrated || token) {
      return;
    }
    (async () => {
      try {
        // Remove resíduo do formato antigo (podia estar protegido por biometria).
        await clearLegacyCredentials();

        const supported = await getSupportedBiometry();
        setBiometryType(supported);

        // Com biometria ativa, NÃO pré-preenchemos a senha (as credenciais ficam
        // protegidas no keychain e só são lidas mediante autenticação biométrica).
        if (biometricEnabled && supported) {
          setIsBiometricChecked(true);
          return;
        }

        const keychainCredentials = await getCredentialsPlain();
        if (
          keychainCredentials &&
          keychainCredentials.username &&
          keychainCredentials.password &&
          keychainCredentials.username !== '' &&
          (keychainCredentials.username?.length > 4 ||
            keychainCredentials.password?.length > 2)
        ) {
          setCredentials({
            username: keychainCredentials.username,
            password: keychainCredentials.password,
          });
          setIsRememberMe(true);
        }
      } catch (err) {}
    })();
  }, [biometricEnabled, hydrated, token]);

  return (
    <ImageBackground
      // source={Images.backgroundCoke}
      style={[
        styles.imageBackground,
        isDesktop && { justifyContent: 'center', alignItems: 'center' },
      ]}>
      <View
        style={[
          styles.container,
          isDesktop && {
            maxWidth: 440,
            width: '100%',
            marginHorizontal: 'auto',
            borderRadius: 16,
            paddingHorizontal: 48,
            paddingTop: 40,
            paddingBottom: 32,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 8 },
            shadowOpacity: 0.15,
            shadowRadius: 24,
            elevation: 16,
          },
        ]}>
        <ScrollView showsVerticalScrollIndicator={false}>
          <Image
            source={Images.ProfileSimbol}
            style={[
              styles.simbol,
              isDesktop && { width: 90, height: 135, marginBottom: 36 },
            ]}
          />
          <View>
            <Input
              title="CNPJ/CPF"
              placeholder="CNPJ/CPF"
              keyboardType="default"
              value={credentials?.username}
              error={fieldErrors.username}
              onChangeText={text => {
                setCredentials({
                  ...credentials,
                  username: text,
                });
                setFieldErrors(prev => ({...prev, username: undefined}));
              }}
            />
            <Input
              title="Senha"
              placeholder="Senha..."
              hasSecurity
              hasForgetPassword
              mt={30}
              forgetPassword={handleNavigateForgetPassword}
              value={credentials?.password}
              error={fieldErrors.password}
              onChangeText={text => {
                setCredentials({...credentials, password: text});
                setFieldErrors(prev => ({...prev, password: undefined}));
              }}
            />
          </View>
          <View style={styles.containerCheckBox}>
            <CheckBox
              description="Lembrar de mim"
              isCheck={isRememberMe}
              onPress={() => setIsRememberMe(!isRememberMe)}
            />
          </View>

          {biometryType && (
            <View style={styles.containerCheckBox}>
              <CheckBox
                description={`Entrar com ${biometryLabel}`}
                isCheck={isBiometricChecked}
                onPress={() => setIsBiometricChecked(!isBiometricChecked)}
              />
            </View>
          )}

          <View style={styles.containerButton}>
            <ButtonComponent title="Entrar" onPress={handleLogin} />
            {biometricEnabled && biometryType && (
              <View style={styles.biometricButton}>
                <ButtonComponent
                  title={`Entrar com ${biometryLabel}`}
                  outlined
                  iconName="fingerprint"
                  onPress={handleBiometricLogin}
                />
              </View>
            )}
            <TouchableOpacity onPress={handleNavigateRegisterCode}>
              <Text style={[styles.validation, isDesktop && { fontSize: 14 }]}>
                Primeiro acesso?{' '}
                <Text style={styles.validation_link}>
                  Valide o seu cadastro!
                </Text>
              </Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={handleNavigateResendToken}>
              <Text style={[styles.validation, isDesktop && { fontSize: 14 }]}>
                Precisa verificar sua conta?
              </Text>
              <Text style={styles.resend_token}>Solicite um novo token</Text>
            </TouchableOpacity>
            {/* <TouchableOpacity onPress={handleNavigateNFSig}>
              <Text style={styles.validation}>
                Deseja consultar a nota fiscal de um pedido?
              </Text>
              <Text style={styles.resend_token}>Acesse aqui!</Text>
            </TouchableOpacity> */}
            <TouchableOpacity
              onPress={() => Linking.openURL('tel:08000615000')}>
              <Text style={styles.call}>
                Não tem cadastro com a Brasal Refrigerantes?
              </Text>
              <Text style={styles.number}>Ligue para 0800 061 5000</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>
    </ImageBackground>
  );
};

export default Login;
